/**
 * IntakeForm — public Step 1 form.
 *
 * Fields: Legal First/Last/Phone/Email, two SMS consent checkboxes (unchecked),
 * single T&C agreement checkbox (unchecked, required), Turnstile, optional
 * "Referred By" text (only on the generic /signup route).
 *
 * When a referral_code is in the URL, looks up the affiliate's display_name
 * and shows "Referred by Jane Doe" + a "not the right person?" reassurance.
 *
 * Consent text + policy labels are read from the consent_disclosures table.
 * The form doesn't render the consent UI until disclosures load — prevents
 * showing raw policy_key strings as fallback text.
 */

import { useEffect, useState } from 'react';
import { UserCheck } from 'lucide-react';
import {
  getAffiliateByCode,
  getConsentDisclosures,
  submitIntake,
} from '../lib/api';
import type { ConsentDisclosure } from '../lib/types';
import TurnstileWidget from '../components/TurnstileWidget';

interface Props {
  referralCode?: string;
  navigate: (path: string) => void;
}

interface FormState {
  applicant_first_name: string;
  applicant_last_name: string;
  applicant_phone: string;
  applicant_email: string;
  referred_by_text: string;
  sms_service_consent: boolean;
  sms_marketing_consent: boolean;
  tc_agreement: boolean;
}

const EMPTY_FORM: FormState = {
  applicant_first_name: '',
  applicant_last_name: '',
  applicant_phone: '',
  applicant_email: '',
  referred_by_text: '',
  sms_service_consent: false,
  sms_marketing_consent: false,
  tc_agreement: false,
};

// Human-friendly fallback labels for the 6 policies referenced in the T&C
// agreement, used only when consent_disclosures.text_content is missing.
const FALLBACK_LABELS: Record<string, string> = {
  terms_of_service: 'Terms of Service',
  privacy_policy: 'Privacy Policy',
  tcpa_compliance: 'TCPA Compliance',
  compliance_guide: 'Compliance Guide',
  acceptable_use: 'Acceptable Use Policy',
  refund_policy: 'Refund Policy',
};

// Default consent statements if the DB rows don't carry text_content.
const FALLBACK_CONSENT_TEXT: Record<string, string> = {
  sms_service_consent_text:
    'I consent to receive service-related text messages from Oasis Auto Konnect LLC, including account notifications, onboarding updates, appointment reminders, and support communications. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help.',
  sms_marketing_consent_text:
    'I consent to receive marketing and promotional text messages from Oasis Auto Konnect LLC. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help.',
};

export default function IntakeForm({ referralCode, navigate: _navigate }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [disclosures, setDisclosures] = useState<ConsentDisclosure[] | null>(null);
  const [affiliateName, setAffiliateName] = useState<string | null>(null);
  const [affiliateLookupTried, setAffiliateLookupTried] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Disclosures fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getConsentDisclosures();
      if (cancelled) return;
      if (!res.success || !res.data) {
        setError(
          res.error ||
            "We're having trouble loading the page. Please refresh and try again."
        );
      } else {
        setDisclosures(res.data);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Affiliate lookup (only when /r/:code)
  useEffect(() => {
    if (!referralCode) return;
    let cancelled = false;
    (async () => {
      const res = await getAffiliateByCode(referralCode);
      if (cancelled) return;
      setAffiliateLookupTried(true);
      if (res.success && res.data) setAffiliateName(res.data.display_name);
    })();
    return () => {
      cancelled = true;
    };
  }, [referralCode]);

  const disclosuresReady = disclosures !== null;
  const disclosuresByKey = (() => {
    const m = new Map<string, ConsentDisclosure>();
    for (const d of disclosures || []) m.set(d.policy_key, d);
    return m;
  })();

  // Returns the text to render for a consent statement (sms_service, sms_marketing, tc).
  // Falls back to the hardcoded statement if the DB row is missing/empty.
  const consentText = (key: string): string => {
    const d = disclosuresByKey.get(key);
    if (d?.text_content && d.text_content.trim()) return d.text_content.trim();
    return FALLBACK_CONSENT_TEXT[key] || '';
  };

  // Returns { label, url } for a policy link, falling back to a friendly label if missing.
  const policyLink = (key: string): { label: string; url: string | null } => {
    const d = disclosuresByKey.get(key);
    const label =
      d?.text_content && d.text_content.trim()
        ? d.text_content.trim()
        : FALLBACK_LABELS[key] || key;
    const url = d?.url || null;
    return { label, url };
  };

  const tcPolicyKeys = [
    'terms_of_service',
    'privacy_policy',
    'tcpa_compliance',
    'compliance_guide',
    'acceptable_use',
    'refund_policy',
  ];

  const canSubmit =
    !!form.applicant_first_name.trim() &&
    !!form.applicant_last_name.trim() &&
    !!form.applicant_email.trim() &&
    !!form.applicant_phone.trim() &&
    form.tc_agreement &&
    !!turnstileToken &&
    disclosuresReady &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !disclosures) return;

    setError(null);
    setSubmitting(true);

    const disclosureVersions: Record<string, string> = {};
    for (const d of disclosures) {
      disclosureVersions[d.policy_key] = d.id;
    }

    const res = await submitIntake({
      applicant_first_name: form.applicant_first_name.trim(),
      applicant_last_name: form.applicant_last_name.trim(),
      applicant_email: form.applicant_email.trim().toLowerCase(),
      applicant_phone: form.applicant_phone.trim(),
      sms_service_consent: form.sms_service_consent,
      sms_marketing_consent: form.sms_marketing_consent,
      tc_agreement: form.tc_agreement,
      referral_code: referralCode || null,
      referred_by_text: referralCode ? null : form.referred_by_text.trim() || null,
      turnstile_token: turnstileToken!,
      disclosure_versions: disclosureVersions,
    });

    setSubmitting(false);

    if (!res.success) {
      setError(res.error || "Something went wrong. Please try again.");
      setTurnstileToken(null);
      if (window.turnstile) {
        try { window.turnstile.reset(); } catch { /* widget not initialized */ }
      }
      return;
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-8 max-w-2xl mx-auto text-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">
          Check your email
        </h1>
        <p className="text-slate-600 dark:text-slate-300 mb-1">
          We just sent a link to <strong>{form.applicant_email}</strong>.
        </p>
        <p className="text-slate-600 dark:text-slate-300">
          Click the link to finish your signup &mdash; you can pause and continue any time.
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-6">
          Didn't get it? Check your spam folder, or{' '}
          <a href="/portal/login" className="text-blue-600 dark:text-blue-400 hover:underline">
            request a fresh link
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-6 sm:p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-white mb-2">
        Get Started with OAK
      </h1>
      <p className="text-slate-600 dark:text-slate-300 mb-6">
        Takes about 5 minutes. You can pause and finish later from any device &mdash; we'll
        email you a link.
      </p>

      {/* Affiliate attribution banner */}
      {referralCode && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 rounded-md px-4 py-3">
          <div className="flex items-start gap-2">
            <UserCheck className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              {affiliateName ? (
                <>
                  <p className="text-blue-900 dark:text-blue-100">
                    Referred by <strong>{affiliateName}</strong>
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    If this isn't the person who referred you, please reach back out to them
                    for the correct link.
                  </p>
                </>
              ) : affiliateLookupTried ? (
                <p className="text-blue-900 dark:text-blue-100">
                  Sign up via referral link
                </p>
              ) : (
                <p className="text-blue-900 dark:text-blue-100">Looking up referrer&hellip;</p>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Legal first name" required>
            <input
              type="text"
              required
              value={form.applicant_first_name}
              onChange={(e) => setForm({ ...form, applicant_first_name: e.target.value })}
              className={inputClasses}
              autoComplete="given-name"
            />
          </Field>
          <Field label="Legal last name" required>
            <input
              type="text"
              required
              value={form.applicant_last_name}
              onChange={(e) => setForm({ ...form, applicant_last_name: e.target.value })}
              className={inputClasses}
              autoComplete="family-name"
            />
          </Field>
        </div>

        <Field label="Phone" required>
          <input
            type="tel"
            required
            value={form.applicant_phone}
            onChange={(e) => setForm({ ...form, applicant_phone: e.target.value })}
            placeholder="(555) 555-5555"
            className={inputClasses}
            autoComplete="tel"
          />
        </Field>

        <Field label="Email" required>
          <input
            type="email"
            required
            value={form.applicant_email}
            onChange={(e) => setForm({ ...form, applicant_email: e.target.value })}
            className={inputClasses}
            autoComplete="email"
          />
        </Field>

        {!referralCode && (
          <Field label="Referred by (optional)">
            <input
              type="text"
              value={form.referred_by_text}
              onChange={(e) => setForm({ ...form, referred_by_text: e.target.value })}
              placeholder="Name of person or company"
              className={inputClasses}
            />
          </Field>
        )}

        {/* Communication Preferences */}
        <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
            Communication Preferences
          </h2>
          {disclosuresReady ? (
            <>
              <ConsentCheckbox
                checked={form.sms_service_consent}
                onChange={(v) => setForm({ ...form, sms_service_consent: v })}
                text={consentText('sms_service_consent_text')}
              />
              <ConsentCheckbox
                checked={form.sms_marketing_consent}
                onChange={(v) => setForm({ ...form, sms_marketing_consent: v })}
                text={consentText('sms_marketing_consent_text')}
              />
            </>
          ) : (
            <DisclosureSkeleton lines={3} />
          )}
        </div>

        {/* T&C Agreement */}
        <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
            Agreement
          </h2>
          {disclosuresReady ? (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                required
                checked={form.tc_agreement}
                onChange={(e) => setForm({ ...form, tc_agreement: e.target.checked })}
                className="mt-0.5 flex-shrink-0 rounded border-slate-300 dark:border-slate-600"
              />
              <span className="text-slate-700 dark:text-slate-200 leading-relaxed">
                I have read and agree to OAK's{' '}
                {tcPolicyKeys.map((key, i) => {
                  const { label, url } = policyLink(key);
                  const sep =
                    i < tcPolicyKeys.length - 1
                      ? i === tcPolicyKeys.length - 2
                        ? ', and '
                        : ', '
                      : '.';
                  return (
                    <span key={key}>
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                          {label}
                        </a>
                      ) : (
                        <span>{label}</span>
                      )}
                      {sep}
                    </span>
                  );
                })}
              </span>
            </label>
          ) : (
            <DisclosureSkeleton lines={2} />
          )}
        </div>

        {/* Turnstile */}
        <div className="pt-3">
          <TurnstileWidget
            onToken={setTurnstileToken}
            onError={() => setTurnstileToken(null)}
            onExpired={() => setTurnstileToken(null)}
          />
        </div>

        {/* Submit */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full px-4 py-3 bg-blue-600 dark:bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Sending…' : 'Continue →'}
          </button>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400 text-center pt-2">
          Already signed up?{' '}
          <a href="/portal/login" className="text-blue-600 dark:text-blue-400 hover:underline">
            Sign back in
          </a>
        </p>
      </form>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

const inputClasses =
  'w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500';

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function ConsentCheckbox({
  checked,
  onChange,
  text,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  text: string;
}) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer mb-3 last:mb-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 flex-shrink-0 rounded border-slate-300 dark:border-slate-600"
      />
      <span className="text-slate-700 dark:text-slate-200 leading-relaxed">{text}</span>
    </label>
  );
}

function DisclosureSkeleton({ lines }: { lines: number }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full" />
      ))}
    </div>
  );
}
